import React, { useState } from 'react';
import { FormControl, InputLabel, MenuItem, Select, Box, Paper, Typography } from '@mui/material';
import { appConfig } from '../config/app-config';

interface ClientSelectorProps {
  onSelectionChange: (client: string, month: string, year: string) => void;
  fontFamily?: string;
}

// Month names for display
const monthNames = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

// Mapping from technical folder names to user-friendly display names
const CLIENT_DISPLAY_NAMES: { [key: string]: string } = {
  'payu_prod': 'PayU',
  'spinny_b2c_prod': 'Spinny B2C', 
  'cars24_prod': 'Cars24',
  'indmoney': 'IndMoney',
  'sk_finance_prod': 'SK Finance',
  'attestr': 'Attestr',
  'autorox': 'AutoRox',
  'ice_vehicle_prod_user': 'ICE Vehicle Lookup'
};

// Get user-friendly name or fallback to technical name
const getDisplayName = (technicalName: string): string => {
  return CLIENT_DISPLAY_NAMES[technicalName] || technicalName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

// Get technical name from display name
const getTechnicalName = (displayName: string): string => {
  const entry = Object.entries(CLIENT_DISPLAY_NAMES).find(([_, display]) => display === displayName);
  return entry ? entry[0] : displayName.toLowerCase().replace(/ /g, '_');
};

export const ClientSelector: React.FC<ClientSelectorProps> = ({ onSelectionChange, fontFamily = 'JetBrains Mono' }) => {
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState('');

  // Generate year options (current year and 5 years back)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 6 }, (_, i) => currentYear - i);

  // Set default month and year if not selected
  React.useEffect(() => {
    if (!selectedMonth) {
      const currentMonth = (new Date().getMonth() + 1).toString().padStart(2, '0');
      setSelectedMonth(currentMonth);
    }
    if (!selectedYear) {
      setSelectedYear(currentYear.toString());
    }
  }, [currentYear, selectedMonth, selectedYear]);

  const handleClientChange = (client: string) => {
    setSelectedClient(client);
    if (client && selectedMonth && selectedYear) {
      onSelectionChange(client, selectedMonth, selectedYear);
    }
  };

  const handleMonthChange = (month: string) => {
    setSelectedMonth(month);
    if (selectedClient && month && selectedYear) {
      onSelectionChange(selectedClient, month, selectedYear);
    }
  };

  const handleYearChange = (year: string) => {
    setSelectedYear(year);
    if (selectedClient && selectedMonth && year) {
      onSelectionChange(selectedClient, selectedMonth, year);
    }
  };

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 3, backgroundColor: '#f8f9fa', fontFamily }}>
      <Typography variant="h6" gutterBottom sx={{ fontFamily, mb: 2 }}>
        📊 Select Client and Reporting Period
      </Typography>
      
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 3 }}>
        <Box>
          <FormControl fullWidth>
            <InputLabel sx={{ fontFamily }}>Select Client</InputLabel>
            <Select
              value={getDisplayName(selectedClient)}
              label="Select Client"
              onChange={(e) => handleClientChange(getTechnicalName(e.target.value))}
              sx={{ fontFamily }}
            >
              {appConfig.availableClients.map((client) => (
                <MenuItem key={client} value={getDisplayName(client)} sx={{ fontFamily }}>
                  {getDisplayName(client)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        
        <Box>
          <FormControl fullWidth>
            <InputLabel sx={{ fontFamily }}>Select Month</InputLabel>
            <Select
              value={selectedMonth}
              label="Select Month"
              onChange={(e) => handleMonthChange(e.target.value)}
              sx={{ fontFamily }}
            >
              {monthNames.map((monthName, index) => (
                <MenuItem 
                  key={index + 1} 
                  value={(index + 1).toString().padStart(2, '0')} 
                  sx={{ fontFamily }}
                >
                  {monthName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        
        <Box>
          <FormControl fullWidth>
            <InputLabel sx={{ fontFamily }}>Select Year</InputLabel>
            <Select
              value={selectedYear}
              label="Select Year"
              onChange={(e) => handleYearChange(e.target.value)}
              sx={{ fontFamily }}
            >
              {yearOptions.map((year) => (
                <MenuItem key={year} value={year.toString()} sx={{ fontFamily }}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>
      
      {selectedClient && selectedMonth && selectedYear && (
        <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary', fontFamily }}>
          📁 Looking for: logs_{selectedClient}_{selectedMonth}-{selectedYear}
        </Typography>
      )}
    </Paper>
  );
}; 